import { useEffect, useRef, useState } from 'react';
import { AppButton, AppScreenScaffold, AppTopBar } from '@core/designsystem';
import type { Corner, Quad } from '@core/files';
import './scan.css';

/**
 * Правка углов листа перед выпрямлением.
 *
 * Автопоиск ошибается: лист на тёмном столе, тень поперёк страницы, вторая бумага
 * рядом. Экран показывает найденную рамку поверх снимка, и любой угол можно
 * переставить пальцем — последнее слово за человеком, а не за алгоритмом.
 *
 * Углы тянутся за ручку **крупнее самой точки**: попасть пальцем в засечку
 * в четыре миллиметра нельзя, и на Android это уже стоило одной переделки.
 */

/** Радиус зоны, в которой касание считается взятием угла. */
const GRAB_RADIUS = 44;

export function ScanEditor({
  photo,
  corners,
  detected,
  busy,
  onCancel,
  onConfirm,
}: {
  photo: Blob;
  corners: Quad;
  /** Нашли ли лист сами: если нет, рамка стоит по краю кадра и правка обязательна. */
  detected: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (corners: Quad) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [quad, setQuad] = useState<Quad>(corners);
  const [dragged, setDragged] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(photo);
    setUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [photo]);

  useEffect(() => setQuad(corners), [corners]);

  /** Экранные координаты → координаты снимка: рамка живёт в его системе. */
  const toPhoto = (clientX: number, clientY: number): Corner | null => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || !size) return null;

    const scale = size.width / rect.width;
    return {
      x: Math.max(0, Math.min(size.width, (clientX - rect.left) * scale)),
      y: Math.max(0, Math.min(size.height, (clientY - rect.top) * scale)),
    };
  };

  const onPointerDown = (event: React.PointerEvent) => {
    const point = toPhoto(event.clientX, event.clientY);
    const rect = box.current?.getBoundingClientRect();
    if (!point || !rect || !size) return;

    const scale = size.width / rect.width;
    const nearest = quad
      .map((corner, index) => ({ index, away: Math.hypot(corner.x - point.x, corner.y - point.y) }))
      .reduce((a, b) => (a.away <= b.away ? a : b));

    // Порог считается в экранных точках, а не в точках снимка: палец у человека
    // одного размера, а снимок бывает и на два мегапикселя, и на двенадцать.
    if (nearest.away / scale > GRAB_RADIUS) return;

    setDragged(nearest.index);
    (event.target as Element).setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (dragged === null) return;

    const point = toPhoto(event.clientX, event.clientY);
    if (!point) return;

    setQuad((current) => current.map((corner, index) => (index === dragged ? point : corner)) as Quad);
  };

  const stop = () => setDragged(null);

  const polygon = size
    ? quad.map((corner) => `${(corner.x / size.width) * 100}% ${(corner.y / size.height) * 100}%`).join(', ')
    : '';

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title="Границы документа"
          subtitle={detected ? 'Поправьте углы, если нужно' : 'Лист не найден — расставьте углы'}
          onBack={onCancel}
        />
      }
      bottomBar={
        <div className="scan__actions">
          <AppButton variant="text" onClick={onCancel}>
            Отмена
          </AppButton>
          <AppButton onClick={() => onConfirm(quad)}>
            {busy ? 'Обрабатываем…' : 'Готово'}
          </AppButton>
        </div>
      }
    >
      <div
        className="scan__frame"
        ref={box}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stop}
        onPointerCancel={stop}
      >
        {url ? (
          <img
            className="scan__photo"
            src={url}
            alt="Снимок документа"
            onLoad={(event) =>
              setSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
          />
        ) : null}

        {size ? (
          <>
            <div className="scan__shade" style={{ clipPath: `polygon(${polygon})` }} />
            {quad.map((corner, index) => (
              <span
                key={index}
                className={`scan__corner ${dragged === index ? 'scan__corner--held' : ''}`}
                style={{
                  left: `${(corner.x / size.width) * 100}%`,
                  top: `${(corner.y / size.height) * 100}%`,
                }}
              />
            ))}
          </>
        ) : null}
      </div>
    </AppScreenScaffold>
  );
}
